/**
 * Icone di interfaccia, disegnate inline.
 *
 * Tratto 2 su griglia 24, estremi arrotondati: lo stesso disegno delle icone
 * Lucide del back-office gemello, senza dipenderne.
 *
 * Nessuna libreria e nessun font di icone: sono una ventina di tratti, e
 * tenerli qui evita sia un pacchetto da centinaia di kilobyte sia una richiesta
 * di rete verso terzi. Tutte ereditano `currentColor`, quindi seguono il tema
 * senza duplicazioni, e sono `aria-hidden`: l'etichetta accessibile la fornisce
 * sempre il testo accanto o l'`aria-label` del controllo che le contiene.
 */

import type { SVGProps } from "react";

type ProprietaIcona = Omit<SVGProps<SVGSVGElement>, "children">;

function Icona({ children, ...proprieta }: ProprietaIcona & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...proprieta}
    >
      {children}
    </svg>
  );
}

export const IconaCruscotto = (p: ProprietaIcona) => (
  <Icona {...p}>
    <rect x="3" y="3" width="7" height="8" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="11" width="7" height="10" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
  </Icona>
);

export const IconaPubblicazioni = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M14 3v4h4" />
    <path d="M9 12h6M9 16h6" />
  </Icona>
);

export const IconaScadenzario = (p: ProprietaIcona) => (
  <Icona {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
    <path d="M12 14v3" />
  </Icona>
);

export const IconaAlberatura = (p: ProprietaIcona) => (
  <Icona {...p}>
    <rect x="3" y="3" width="7" height="5" rx="1.2" />
    <rect x="14" y="10" width="7" height="5" rx="1.2" />
    <rect x="14" y="17" width="7" height="4" rx="1.2" />
    <path d="M6.5 8v9.5a1.5 1.5 0 0 0 1.5 1.5h6" />
    <path d="M6.5 12.5h7.5" />
  </Icona>
);

export const IconaSchemi = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="m12 3 9 5-9 5-9-5 9-5Z" />
    <path d="m3 13 9 5 9-5" />
    <path d="m3 17 9 4 9-4" />
  </Icona>
);

export const IconaUtenti = (p: ProprietaIcona) => (
  <Icona {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5.6" />
    <path d="M17.5 20a5.5 5.5 0 0 0-2-4.2" />
  </Icona>
);

export const IconaEnti = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="M3 21h18" />
    <path d="M4 21V10l8-5 8 5v11" />
    <path d="M9 21v-6h6v6" />
    <path d="M8 11h.01M12 11h.01M16 11h.01" />
  </Icona>
);

export const IconaConformita = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="M12 3 5 6v5.5c0 4.3 2.9 8.2 7 9.5 4.1-1.3 7-5.2 7-9.5V6l-7-3Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </Icona>
);

export const IconaProfilo = (p: ProprietaIcona) => (
  <Icona {...p}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </Icona>
);

export const IconaChiaro = (p: ProprietaIcona) => (
  <Icona {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Icona>
);

export const IconaScuro = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </Icona>
);

export const IconaSistema = (p: ProprietaIcona) => (
  <Icona {...p}>
    <rect x="2.5" y="4" width="19" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </Icona>
);

export const IconaChevron = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="m9 5 7 7-7 7" />
  </Icona>
);

export const IconaChiudi = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Icona>
);

export const IconaEsci = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
    <path d="M10 16 6 12l4-4" />
    <path d="M6 12h9" />
  </Icona>
);

export const IconaDocumento = (p: ProprietaIcona) => (
  <Icona {...p}>
    <path d="M6 3h8l4 4v14H6V3Z" />
    <path d="M14 3v4h4" />
  </Icona>
);
